/**
 * Subscription Service
 * Handles in-app purchase logic using cordova-plugin-purchase
 *
 * IMPORTANT: All purchases are validated server-side via validateSubscriptionWithBackend()
 * The backend validates with Apple/Google APIs and updates the user's plan.
 */

import { Capacitor } from '@capacitor/core';
import { trackGaConversion } from '../utils/ga';
import { ALL_PRODUCT_IDS } from '../config/subscriptions.config';
import { validateMobilePurchaseWithBackend } from './subscription-backend.service';
import { fireAndForget } from '../utils/fireAndForget';

// Extend Window interface to include CdvPurchase
declare global {
  interface Window {
    CdvPurchase?: {
      store: CdvPurchaseStore;
      LogLevel: {
        DEBUG: number;
      };
      ProductType: {
        PAID_SUBSCRIPTION: string;
      };
      Platform: {
        APPLE_APPSTORE: string;
        GOOGLE_PLAY: string;
      };
      ErrorCode?: {
        PAYMENT_CANCELLED: number;
      };
    };
  }
}

/**
 * What v13 of the plugin resolves `order()` and `restorePurchases()` with
 * when they do not go through (CdvPurchase.IError, www/store.d.ts). They do
 * NOT reject: a cancel or a failure resolves with this, success with
 * undefined.
 */
interface CdvPurchaseError {
  isError: true;
  code: number;
  message: string;
}

/** ErrorCode.PAYMENT_CANCELLED: ERROR_CODES_BASE (6777000) + 6, www/store.js. */
const PAYMENT_CANCELLED_FALLBACK = 6777006;

/**
 * The outcome of a store action the user can walk away from. 'cancelled':
 * the user closed the store sheet, show nothing. A failure rejects with the
 * plugin's message.
 */
export type StoreActionOutcome = 'done' | 'cancelled';

function isPluginError(result: unknown): result is CdvPurchaseError {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { isError?: unknown }).isError === true
  );
}

/** Read the plugin's answer: undefined is done, a cancel is 'cancelled', anything else throws. */
function readPluginResult(result: unknown): StoreActionOutcome {
  if (!isPluginError(result)) return 'done';
  const cancelled = window.CdvPurchase?.ErrorCode?.PAYMENT_CANCELLED ?? PAYMENT_CANCELLED_FALLBACK;
  if (result.code === cancelled) return 'cancelled';
  throw new Error(result.message || 'The store refused the request');
}

interface CdvPurchaseProduct {
  id: string;
  title?: string;
  description?: string;
  owned?: boolean;
  pricing?: {
    price?: string;
    /** The price in millionths of the currency unit (www/store.d.ts PricingPhase). */
    priceMicros?: number;
    currency?: string;
  };
  expiryDate?: Date;
  getOffer?: () => CdvPurchaseOffer | null;
  products?: Array<{ id: string }>;
  productId?: string;
  purchaseToken?: string;
  transactionId?: string;
}

interface CdvPurchaseOffer {
  order: () => Promise<CdvPurchaseError | undefined>;
}

interface CdvPurchaseTransaction {
  verify: () => void;
  finish: () => void;
  products?: Array<{ id: string }>;
  productId?: string;
  platform?: string;
  /** Android / Play Store */
  purchaseId?: string;
  transactionId?: string;
  purchaseToken?: string;
  nativePurchase?: {
    orderId?: string;
    packageName?: string;
    productId?: string;
    purchaseTime?: number;
    purchaseState?: number;
    purchaseToken?: string;
    autoRenewing?: boolean;
    acknowledged?: boolean;
    signature?: string;
    receipt?: string;
  };

  sourceReceipt?: {
    transactions?: CdvPurchaseTransaction[];
    appStoreReceipt?: string; // iOS only
  };
}

interface CdvPurchaseStore {
  verbosity: number;
  register: (products: Array<{ id: string; type: string; platform: string }>) => void;
  when: () => {
    productUpdated: (callback: (product: CdvPurchaseProduct) => void) => void;
    approved: (callback: (transaction: CdvPurchaseTransaction) => void) => void;
    verified: (callback: (receipt: CdvPurchaseTransaction) => void) => void;
    finished: (callback: (transaction: CdvPurchaseTransaction) => void) => void;
    error: (callback: (error: Error) => void) => void;
  };
  initialize: (platforms: string[]) => void;
  get: (productId: string) => CdvPurchaseProduct | null;
  restorePurchases: () => Promise<CdvPurchaseError | undefined>;
  update: () => Promise<void>;
}

interface SubscriptionData {
  productId: string;
  title: string;
  description: string;
  price?: string;
  expiryDate?: Date;
}

type UpdateCallback = (product: CdvPurchaseProduct | null, status?: string) => void;
type ErrorCallback = (error: Error) => void;
type SubscriptionValidatedCallback = (data: SubscriptionData) => void;

// Store reference (will be set from the store)
let store: CdvPurchaseStore | null = null;

// Callback to notify when subscription is validated
let onSubscriptionValidated: SubscriptionValidatedCallback | null = null;

/**
 * GA4 revenue for an in-app purchase (b-184): the store's own price for the
 * product (priceMicros + currency, what the user was charged in their
 * storefront) and the items line Monetization reports need, as the web
 * purchase sends. Nothing when the store gave no price, rather than a 0
 * that would read as a free purchase.
 */
function gaRevenue(productId: string): Record<string, unknown> {
  const product = store?.get(productId);
  const micros = product?.pricing?.priceMicros;
  const currency = product?.pricing?.currency;
  if (!currency || typeof micros !== 'number' || micros <= 0) return {};
  const value = micros / 1_000_000;
  return {
    currency,
    value,
    items: [
      { item_id: productId, item_name: product?.title || productId, price: value, quantity: 1 },
    ],
  };
}

/**
 * Initialize the in-app purchase store
 */
export const initializeStore = (onUpdate?: UpdateCallback, onError?: ErrorCallback): boolean => {
  // Only initialize on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('IAP: Not on native platform, skipping initialization');
    return false;
  }

  // Check if CdvPurchase is available
  if (!window.CdvPurchase) {
    console.warn(
      'IAP: cordova-plugin-purchase not found - this is normal on web or if plugin is not installed',
    );
    if (onError) {
      onError(new Error('Plugin not available'));
    }
    return false;
  }

  try {
    store = window.CdvPurchase.store;

    // Set log level for debugging (remove in production)
    store.verbosity = window.CdvPurchase.LogLevel.DEBUG;

    // Register products
    ALL_PRODUCT_IDS.forEach((productId) => {
      store!.register([
        {
          id: productId,
          type: window.CdvPurchase!.ProductType.PAID_SUBSCRIPTION,
          platform:
            Capacitor.getPlatform() === 'ios'
              ? window.CdvPurchase!.Platform.APPLE_APPSTORE
              : window.CdvPurchase!.Platform.GOOGLE_PLAY,
        },
      ]);
    });

    // Listen to product updates
    store.when().productUpdated((product: CdvPurchaseProduct) => {
      console.log('IAP: Product updated:', product);
      if (onUpdate) {
        onUpdate(product);
      }
    });

    // Listen to approved transactions (after successful purchase)
    // store.when().approved((transaction: CdvPurchaseTransaction) => {
    //   console.log('IAP: Transaction approved:', transaction);
    //   transaction.verify();
    // });

    // Listen to approved transactions (after successful purchase)
    store.when().approved((transaction: CdvPurchaseTransaction) => {
      const productId = transaction.products?.[0]?.id || transaction.productId;

      console.log('IAP: Transaction approved:', {
        transactionId: transaction.transactionId,
        productId: productId,
        platform: transaction.platform,
      });

      // Ignore ONLY the app-level transaction
      if (productId?.startsWith('app.')) {
        console.log('IAP: Ignoring app-level transaction:', productId);
        return;
      }

      console.log(
        'IAP: Verifying transaction:',
        transaction.transactionId,
        'for product:',
        productId,
      );
      transaction.verify();
    });

    // CRITICAL: Send to backend for server-side validation
    // Listen to verified transactions (after receipt validation)
    store.when().verified(
      fireAndForget(async (receipt: CdvPurchaseTransaction) => {
        console.log('IAP: Receipt verified locally, full object:', receipt);

        let productId: string | undefined;
        let purchaseTransactionId: string | undefined;

        if (receipt.sourceReceipt?.transactions && receipt.sourceReceipt.transactions.length > 0) {
          const transactions = receipt.sourceReceipt.transactions;
          const latestTransaction = transactions[transactions.length - 1];
          productId = latestTransaction.products?.[0]?.id || latestTransaction.productId;
          // The id lives on the TRANSACTION, not the VerifiedReceipt (Codex
          // P2 on #816 round 2) — same descent the productId needs.
          purchaseTransactionId = latestTransaction.transactionId;
        } else {
          productId = receipt.products?.[0]?.id || receipt.productId;
          purchaseTransactionId = receipt.transactionId;
        }

        console.log('IAP: Extracted productId:', productId);

        if (!productId || productId.startsWith('app.')) {
          console.log('IAP: Ignoring app-level or invalid verified receipt:', productId);
          receipt.finish();
          return;
        }

        console.log('IAP: Sending product transaction to backend for productId:', productId);

        try {
          const platform = Capacitor.getPlatform() as 'ios' | 'android';

          // Shape sent to the backend mobile-purchase validation endpoint.
          // platform-specific fields (`receipt_data` for iOS, `purchase_token`
          // for Android) are populated below.
          interface MobilePurchaseValidationData {
            platform: 'ios' | 'android';
            product_id: string;
            receipt_data?: string;
            purchase_token?: string;
          }
          const validationData: MobilePurchaseValidationData = {
            platform: platform,
            product_id: productId,
          };

          if (platform === 'ios') {
            // For iOS, extract the appStoreReceipt from nativeData. The Capacitor
            // IAP plugin types `sourceReceipt` permissively; narrow to the field
            // we read.
            const nativeData = (
              receipt.sourceReceipt as { nativeData?: { appStoreReceipt?: string } } | undefined
            )?.nativeData;
            const receiptData = nativeData?.appStoreReceipt;

            if (!receiptData) {
              console.error('IAP: No receipt data found for iOS purchase');
              if (onError) {
                onError(new Error('No receipt data found'));
              }
              return;
            }

            validationData.receipt_data = receiptData;
            console.log('IAP: iOS receipt data length:', receiptData.length);
          } else {
            // For Android, use purchase token
            const transaction = receipt.sourceReceipt?.transactions?.[0] || receipt;
            const purchaseToken =
              transaction.nativePurchase?.purchaseToken || transaction.purchaseToken || '';

            if (!purchaseToken) {
              console.error('IAP: No purchase token found for Android purchase');
              if (onError) {
                onError(new Error('No purchase token found'));
              }
              return;
            }

            validationData.purchase_token = purchaseToken;
          }

          console.log('IAP: Sending to backend for validation:', {
            platform: validationData.platform,
            product_id: validationData.product_id,
            has_receipt_data: !!validationData.receipt_data,
            has_purchase_token: !!validationData.purchase_token,
          });

          const result = await validateMobilePurchaseWithBackend(validationData);

          if (result.success && result.data) {
            console.log('IAP: Backend validation successful:', result);
            // GA phase 2 purchase fires ONLY behind backend validation
            // (Codex P1 on #816): a locally-verified receipt the server
            // rejects — or the app-level receipts filtered above — must
            // never reach campaign optimization. transaction_id lets GA4
            // dedup replayed callbacks (restores re-verify old receipts).
            trackGaConversion('purchase', {
              store: receipt.platform ?? 'unknown',
              product_id: productId,
              ...(purchaseTransactionId ? { transaction_id: purchaseTransactionId } : {}),
              ...gaRevenue(productId),
            });
            receipt.finish();

            if (onSubscriptionValidated) {
              const subscriptionData: SubscriptionData = {
                productId: result.data.product_id || '',
                title: '',
                description: '',
                price: result.data.price?.toString(),
                expiryDate: result.data.expires_at ? new Date(result.data.expires_at) : undefined,
              };
              onSubscriptionValidated(subscriptionData);
            }
            if (onUpdate) {
              onUpdate(null, 'validated');
            }
          } else {
            console.error('IAP: Backend validation failed:', result.message);
            if (onError) {
              onError(new Error(result.message || 'Backend validation failed'));
            }
          }
        } catch (error) {
          console.error('IAP: Backend validation error:', error);
          if (onError) {
            onError(error instanceof Error ? error : new Error(String(error)));
          }
        }
      }),
    );

    // Listen to finished transactions
    store.when().finished((transaction: CdvPurchaseTransaction) => {
      console.log('IAP: Transaction finished:', transaction);
      if (onUpdate) {
        onUpdate(null, 'finished');
      }
    });

    // Listen to errors
    // store.when().error((error: Error) => {
    //   console.error('IAP: Error:', error);
    //   if (onError) {
    //     onError(error);
    //   }
    // });

    // Initialize the store
    store.initialize([
      Capacitor.getPlatform() === 'ios'
        ? window.CdvPurchase.Platform.APPLE_APPSTORE
        : window.CdvPurchase.Platform.GOOGLE_PLAY,
    ]);

    console.log('IAP: Store initialized successfully');
    return true;
  } catch (error) {
    console.error('IAP: Failed to initialize store:', error);
    if (onError) {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
    return false;
  }
};

/**
 * Get a product by ID
 */
export const getProduct = (productId: string): CdvPurchaseProduct | null => {
  if (!store) return null;
  return store.get(productId);
};

/**
 * Get all products
 */
export const getAllProducts = (): CdvPurchaseProduct[] => {
  if (!store) return [];
  return ALL_PRODUCT_IDS.map((id) => store!.get(id)).filter(
    (p): p is CdvPurchaseProduct => p !== null,
  );
};

/**
 * Check if user has an active subscription
 */
export const hasActiveSubscription = (): boolean => {
  if (!store) return false;

  for (const productId of ALL_PRODUCT_IDS) {
    const product = store.get(productId);
    if (product && product.owned) {
      return true;
    }
  }

  return false;
};

/**
 * Get active subscription details
 */
export const getActiveSubscription = (): SubscriptionData | null => {
  if (!store) return null;

  for (const productId of ALL_PRODUCT_IDS) {
    const product = store.get(productId);
    if (product && product.owned) {
      return {
        productId: product.id,
        title: product.title || '',
        description: product.description || '',
        price: product.pricing?.price,
        expiryDate: product.expiryDate,
      };
    }
  }

  return null;
};

/**
 * Purchase a subscription. 'done': the order went through (the plan itself
 * lands via the verified callback); 'cancelled': the user closed the store
 * sheet. A failure rejects with the plugin's message.
 */
export const purchaseSubscription = async (productId: string): Promise<StoreActionOutcome> => {
  console.log('IAP: Starting purchase for productId:', productId);
  if (!store) {
    throw new Error('Store not initialized');
  }

  const product = store.get(productId);
  if (!product) {
    throw new Error('Product not found');
  }

  try {
    const offer = product.getOffer?.();
    if (!offer) {
      throw new Error('No offer available for this product');
    }

    const outcome = readPluginResult(await offer.order());
    console.log('IAP: Purchase', outcome, 'for productId:', productId);
    return outcome;
  } catch (error) {
    console.error('IAP: Purchase failed:', error);
    throw error;
  }
};

/**
 * Restore purchases. Same contract as purchaseSubscription.
 */
export const restorePurchases = async (): Promise<StoreActionOutcome> => {
  if (!store) {
    throw new Error('Store not initialized');
  }

  try {
    return readPluginResult(await store.restorePurchases());
  } catch (error) {
    console.error('IAP: Restore failed:', error);
    throw error;
  }
};

/**
 * Refresh store data
 */
export const refreshStore = async (): Promise<void> => {
  if (!store) return;

  try {
    await store.update();
  } catch (error) {
    console.error('IAP: Refresh failed:', error);
  }
};

/**
 * Get store instance (for advanced usage)
 */
export const getStoreInstance = (): CdvPurchaseStore | null => store;

/**
 * Set callback for when subscription is validated with backend
 * This is called after successful backend validation with the subscription data
 */
export const setOnSubscriptionValidated = (callback: SubscriptionValidatedCallback): void => {
  onSubscriptionValidated = callback;
};

/**
 * Check if running on a native platform (iOS or Android)
 */
export const isNativePlatform = (): boolean => Capacitor.isNativePlatform();

/**
 * Get current platform ('ios', 'android', or 'web')
 */
export const getCurrentPlatform = (): string => {
  if (!Capacitor.isNativePlatform()) return 'web';
  return Capacitor.getPlatform();
};
